<template>
  <div class="test-renderer">
    <div v-if="testData">
      <TestMap :testData="testData" :answers="answers" :currentPageIndex="currentPageIndex"
        :publication="{ id: 'preview', mode: 'PREVIEW' }"
        @navigate-to-page="handleNavigateToPage" />
      <div class="row">
        <div class="col-12">
          <div class="container wrapper">

            <transition name="slide-down">
              <TestMap v-if="showTestMap" :testData="testData" :answers="answers" :currentPageIndex="currentPageIndex"
                :publication="{ id: 'preview', mode: 'PREVIEW' }"
                @navigate-to-page="handleNavigateToPage" />
            </transition>

            <h1>{{ testData.title }}</h1>
            <div class="row">
              <div class="col">
                <p>{{ testData.Description }}</p>
              </div>
              <div class="col text-end me-4">
                <b>PAGE {{ currentPageIndex + 1 }}</b>
              </div>
            </div>

            <!-- Stimulus en haut si configuré -->
            <div v-if="shouldShowStimulus('top')" 
                 class="stimulus-container stimulus-top" 
                 :class="{ 'stimulus-sticky': currentPageParameters.stimulus?.sticky }"
                 v-html="processedStimulusContent"></div>

            <!-- 🎛️ Contenu principal avec disposition en colonnes si configuré -->
            <div v-if="currentPageParameters.layout?.columnType === 'double' && currentPageParameters.stimulus?.visible && currentPageParameters.stimulus?.position === 'left'" class="page-double-column" :style="getPageStyles">
              <!-- Stimulus à gauche -->
              <div class="stimulus-column" :style="{ width: leftColumnWidth }">
                <div class="stimulus-container stimulus-left"
                     :class="{ 'stimulus-sticky': currentPageParameters.stimulus?.sticky }"
                     v-html="processedStimulusContent"></div>
              </div>
              <!-- Séparateur redimensionnable -->
              <div v-if="currentPageParameters.layout?.resizableSeparator"
                   class="column-separator"
                   @mousedown="startResize"
                   @touchstart="startResize">
                <div class="separator-handle">
                  <i class="fas fa-grip-lines-vertical"></i>
                </div>
              </div>
              <!-- Items à droite dans une seule colonne -->
              <div class="items-column" :style="{ width: rightColumnWidth }">
                <div class="pageContainer" :class="getPageClasses">
                  <template v-if="renderedChildren.length > 0">
                    <div class="itemContainer" v-for="child in renderedChildren" :key="child.id">
                      <component :is="child.component" v-bind="{
                        ...child.props,
                        questionIndexMap
                      }" />
                    </div>
                  </template>
                  <div v-else class="alert alert-warning mt-4">
                    Aucun contenu à afficher pour cette page.
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="currentPageParameters.layout?.columnType === 'double' && currentPageParameters.stimulus?.visible && currentPageParameters.stimulus?.position === 'right'" class="page-double-column" :style="getPageStyles">
              <!-- Items à gauche dans une seule colonne -->
              <div class="items-column" :style="{ width: leftColumnWidth }">
                <div class="pageContainer" :class="getPageClasses">
                  <template v-if="renderedChildren.length > 0">
                    <div class="itemContainer" v-for="child in renderedChildren" :key="child.id">
                      <component :is="child.component" v-bind="{
                        ...child.props,
                        questionIndexMap
                      }" />
                    </div>
                  </template>
                  <div v-else class="alert alert-warning mt-4">
                    Aucun contenu à afficher pour cette page.
                  </div>
                </div>
              </div>
              <!-- Séparateur redimensionnable -->
              <div v-if="currentPageParameters.layout?.resizableSeparator"
                   class="column-separator"
                   @mousedown="startResize"
                   @touchstart="startResize">
                <div class="separator-handle">
                  <i class="fas fa-grip-lines-vertical"></i>
                </div>
              </div>
              <!-- Stimulus à droite -->
              <div class="stimulus-column" :style="{ width: rightColumnWidth }">
                <div class="stimulus-container stimulus-right"
                     :class="{ 'stimulus-sticky': currentPageParameters.stimulus?.sticky }"
                     v-html="processedStimulusContent"></div>
              </div>
            </div>

            <!-- Mode colonne simple ou pas de stimulus en colonnes -->
            <div v-else class="pageContainer" :class="getPageClasses" :style="getPageStyles">
              <template v-if="renderedChildren.length > 0">
                <div class="itemContainer" v-for="child in renderedChildren" :key="child.id">
                  <component :is="child.component" v-bind="{
                    ...child.props,
                    questionIndexMap
                  }" />
                </div>
              </template>

              <div v-else class="alert alert-warning mt-4">
                Aucun contenu à afficher pour cette page.
              </div>

              <!-- Stimulus en bas si configuré -->
              <div v-if="shouldShowStimulus('bottom')" 
                   class="stimulus-container stimulus-bottom" 
                   :class="{ 'stimulus-sticky': currentPageParameters.stimulus?.sticky }"
                   v-html="processedStimulusContent"></div>

              <div class="Endpage" v-if="lastPageReached">
                <LastPage :testData="testData" />
              </div>
            </div>

            <!-- 🔄 Boutons de navigation TOUJOURS VISIBLES - en dehors du système de colonnes -->
            <div class="container navigation-buttons mt-4 mb-4">
              <div class="row">
                <div class="col">
                  <button v-if="currentPageIndex > 0" class="btn btn-primary me-2" :disabled="readOnly"
                    @click="onPrevPage">Previous
                    page</button>
                </div>
                <div class="col text-end">
                  <button v-if="currentPage && currentPageIndex + 1 < testData.pages.length" class="btn btn-primary"
                    :disabled="readOnly" @click="onNextPage">Next page</button>

                  <button v-if="currentPage && currentPageIndex + 1 === testData.pages.length && !readOnly"
                    class="btn btn-primary sendToServer" @click="onSendResults">Send results</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="loading" class="text-center mt-5">
      <p><i>Loading test preview…</i></p>
    </div>

    <div v-else-if="error" class="text-center mt-5">
      <div class="alert alert-danger">
        <h4>Erreur de chargement</h4>
        <p>{{ error }}</p>
        <small>Test ID: {{ testId }}</small>
        <small>API URL: {{ apiUrl }}</small>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script setup>
import TestMap from './TestMap.vue'
import LastPage from './LastPage.vue'
import Footer from '../views/footer.vue'
import { useRoute } from 'vue-router'
import { ref, onMounted, computed, watch } from 'vue'
import { createInteraction } from '../interactions/InteractionFactory'
import api from '../api'


const props = defineProps({
  questionIndexMap: Array,
  readOnly: Boolean,
  currentPageIndex: Number,
  lastPageReached: Boolean,
  answers: Object,
  stopTimer: Boolean,
  onPrevPage: Function,
  onNextPage: Function,
  onSendResults: Function,
  handleNavigateToPage: Function
})

const route = useRoute()
const testData = ref(null)
const loading = ref(true)
const error = ref(null)

// fallback locaux pour preview autonome
const readOnly = props.readOnly ?? true
const lastPageReached = props.lastPageReached ?? false
const answers = props.answers ?? {}
const onSendResults = props.onSendResults ?? (() => { })
const stopTimer = props.stopTimer ?? false

const showTestMap = ref(false);

const isAutonomous = props.onNextPage === undefined;
const currentPageIndex = isAutonomous
  ? ref(0)
  : props.currentPageIndex;

const onNextPage = isAutonomous
  ? () => {
    if (currentPageIndex.value + 1 < testData.value.pages.length) {
      currentPageIndex.value++;
    }
  }
  : props.onNextPage;

const onPrevPage = isAutonomous
  ? () => {
    if (currentPageIndex.value > 0) {
      currentPageIndex.value--;
    }
  }
  : props.onPrevPage;

defineEmits(['time-up', 'toggle-test-map'])

const testId = route.query.testId

const handleNavigateToPage = isAutonomous
  ? (pageIndex) => {
    console.log("📌 handleNavigateToPage appelé avec :", pageIndex);
    currentPageIndex.value = pageIndex;
  }
  : props.handleNavigateToPage;

// 🔧 Configuration dynamique des URLs API
const isDocker = import.meta.env.VITE_DOCKER_ON === "istrue";

const apiUrl = computed(() => {
  if (!testId) return '';

  if (isDocker) {
    // En mode Docker, utiliser l'URL interne
    const dockerApiBase = import.meta.env.VITE_TESTRUNNER_API;
    return `${dockerApiBase}/tests/${testId}`;
  } else {
    // En mode dev, utiliser l'URL de l'API backend
    const devApiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3011';
    return `${devApiBase}/tests/${testId}`;
  }
});

const questionIndexMap = computed(() => {
  if (!testData.value || !Array.isArray(testData.value.pages)) return {};

  let index = 1;
  const map = {};

  for (const page of testData.value.pages) {
    for (const child of page.children || []) {
      const el = testData.value.elements.find(e => e.el_ID === child.id);
      if (el && el.el_Type !== 'message') {
        map[el.el_ID] = index++;
      }
    }
  }

  return map;
});

const currentPage = computed(() => {
  const index = isAutonomous
    ? currentPageIndex.value
    : props.currentPageIndex;

  if (
    !testData.value ||
    !Array.isArray(testData.value.pages) ||
    testData.value.pages.length === 0 ||
    index >= testData.value.pages.length
  ) {
    return null;
  }
  return testData.value.pages[index];
});

// 🎛️ Paramètres effectifs de la page courante
const currentPageParameters = computed(() => {
  if (!currentPage.value?.effectiveParameters) {
    console.log('🎛️ Pas de paramètres pour la page courante, utilisation des défauts');
    return {
      layout: { columnType: 'single', singleColumnWidth: 80 },
      stimulus: { visible: false, content: '', position: 'top' },
      theme: { mode: 'light' }
    };
  }
  
  console.log('🎛️ Paramètres de la page courante:', currentPage.value.effectiveParameters);
  return currentPage.value.effectiveParameters;
});

// 🎛️ Classes CSS pour la mise en page
const getPageClasses = computed(() => {
  const params = currentPageParameters.value;
  const classes = ['page-with-parameters'];
  
  // Classes pour le layout
  if (params.layout?.columnType) {
    classes.push(`layout-${params.layout.columnType}`);
  }
  
  // Classes pour le thème
  if (params.theme?.mode) {
    classes.push(`theme-${params.theme.mode}`);
  }
  
  // Classe pour la largeur
  if (params.layout?.columnType === 'single') {
    classes.push(`width-${params.layout.singleColumnWidth || 80}`);
  }
  
  return classes.join(' ');
});

// 🎛️ Styles CSS dynamiques
const getPageStyles = computed(() => {
  const params = currentPageParameters.value;
  const styles = {};
  
  // Largeur pour colonne simple
  if (params.layout?.columnType === 'single') {
    styles['--content-width'] = `${params.layout.singleColumnWidth || 80}%`;
  }
  
  // Distribution des colonnes
  if (params.layout?.columnType === 'double') {
    const distribution = params.layout.contentDistribution || '50-50';
    const [left, right] = distribution.split('-');
    styles['--left-column'] = `${left}%`;
    styles['--right-column'] = `${right}%`;
  }
  
  return styles;
});

// 🎛️ Détermine si le stimulus doit être affiché à une position donnée
const shouldShowStimulus = (position) => {
  const params = currentPageParameters.value;
  return params.stimulus?.visible && params.stimulus?.position === position && params.stimulus?.content;
};

// 📏 Gestion du redimensionnement des colonnes
const isResizing = ref(false);
const leftColumnWidth = ref('calc(50% - 4px)');
const rightColumnWidth = ref('calc(50% - 4px)');

// Initialiser les largeurs des colonnes en fonction des paramètres
watch(currentPageParameters, (params) => {
  if (params.layout?.columnType === 'double' && !isResizing.value) {
    const distribution = params.layout.contentDistribution || '50-50';
    const [left, right] = distribution.split('-');

    // Si le séparateur est activé, soustraire 4px de chaque côté pour le séparateur de 8px
    if (params.layout?.resizableSeparator) {
      leftColumnWidth.value = `calc(${left}% - 4px)`;
      rightColumnWidth.value = `calc(${right}% - 4px)`;
    } else {
      leftColumnWidth.value = `${left}%`;
      rightColumnWidth.value = `${right}%`;
    }
  }
}, { immediate: true });

const startResize = (e) => {
  e.preventDefault();
  isResizing.value = true;

  const onMouseMove = (moveEvent) => {
    if (!isResizing.value) return;

    const container = moveEvent.target.closest('.page-double-column');
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const mouseX = moveEvent.clientX || moveEvent.touches?.[0]?.clientX;

    if (!mouseX) return;

    // Calculer le pourcentage
    const percentage = ((mouseX - containerRect.left) / containerRect.width) * 100;

    // Limiter entre 20% et 80% pour éviter des colonnes trop petites
    const clampedPercentage = Math.max(20, Math.min(80, percentage));

    // Soustraire 4px de chaque côté pour le séparateur de 8px
    leftColumnWidth.value = `calc(${clampedPercentage}% - 4px)`;
    rightColumnWidth.value = `calc(${100 - clampedPercentage}% - 4px)`;
  };

  const onMouseUp = () => {
    isResizing.value = false;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    document.removeEventListener('touchmove', onMouseMove);
    document.removeEventListener('touchend', onMouseUp);
  };

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
  document.addEventListener('touchmove', onMouseMove);
  document.addEventListener('touchend', onMouseUp);
};

// 🎬 Convertit le contenu du stimulus en remplaçant les placeholders vidéo par de vraies vidéos
const processedStimulusContent = computed(() => {
  const params = currentPageParameters.value;
  if (!params.stimulus?.content) return '';
  
  let content = params.stimulus.content;
  
  // Remplacer les placeholders vidéo par de vraies vidéos
  content = content.replace(
    /<p data-video-url="([^"]*)" data-video-size="([^"]*)"[^>]*>📹 VIDÉO:[^<]*<\/p>/g,
    (match, videoUrl, videoSize) => {
      console.log('🎬 Conversion placeholder vers vidéo:', videoUrl, videoSize);
      
      // Créer le HTML de la vidéo avec les mêmes paramètres optimisés
      const sizeClass = videoSize === 'full' ? '' : `video-${videoSize}`;
      return `<div class="video-container ${sizeClass}"><iframe 
        src="${videoUrl}" 
        frameborder="0" 
        allowfullscreen 
        allow="picture-in-picture" 
        style="width:100%;height:300px;border-radius:8px;"
        loading="lazy"
        referrerpolicy="strict-origin-when-cross-origin"
      ></iframe></div>`;
    }
  );
  
  return content;
});

function getQuestion(child) {
  const question = testData.value.elements.find(el => el.el_ID === child.id)
  if (!question) {
    console.warn("❗️ Question non trouvée pour child.id =", child.id)
  }
  return question
}

const renderedChildren = computed(() => {
  if (!currentPage.value || !Array.isArray(currentPage.value.children)) return [];

  return currentPage.value.children.map(child => {
    const question = getQuestion(child);
    if (!question) return null;

    const interaction = createInteraction(question, testData.value);
    const rendered = interaction.render();

    return {
      id: child.id,
      component: rendered.component,
      props: {
        ...rendered.props,
        numOrder: questionIndexMap.value?.[question.el_ID] ?? null,
        questionIndexMap: questionIndexMap.value
      }
    };
  }).filter(Boolean);
});

onMounted(async () => {
  try {
    loading.value = true;
    error.value = null;

    console.log("🔍 Chargement du test avec ID:", testId);
    console.log("🔍 Route query params:", route.query);
    
    if (!testId) {
      throw new Error("Aucun ID de test fourni");
    }

    // 🎬 CORRECTION : Vérifier si nous avons un token preview
    const previewToken = route.query.previewToken;
    console.log("🔍 Preview token présent:", !!previewToken);
    
    let response;

    if (previewToken) {
      // ✅ Mode preview - utiliser la route API /preview
      console.log("🎬 Mode preview détecté, utilisation de /api/tests/:id/preview");
      console.log("🔑 Preview token:", previewToken.substring(0, 20) + "...");
      
      response = await api.get(`/tests/${testId}/preview?previewToken=${previewToken}`);
      console.log("✅ Réponse preview reçue");
      
    } else {
      // 👤 Mode normal avec authentification étudiant
      console.log("👤 Mode étudiant normal, utilisation de /api/tests/:id");
      response = await api.get(`/tests/${testId}`);
      console.log("✅ Réponse normale reçue");
    }

    testData.value = response.data;
    console.log("✅ testData assigné :", testData.value?.title);
    console.log("📄 Première page :", testData.value.pages?.[0]?.children?.length, "éléments");

  } catch (err) {
    console.error('❌ Erreur de chargement du test :', err);
    console.error('❌ Détails erreur:', err.response?.data);
    error.value = err.response?.data?.message || err.message;
  } finally {
    loading.value = false;
  }
})
</script>

<style scoped>
.wrapper {
  margin-top: 70px;
}

.sendToServer {
  margin-left: 10px;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.4s ease-in-out, opacity 0.4s ease-in-out;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-down-enter-to,
.slide-down-leave-from {
  transform: translateY(0);
  opacity: 1;
}

.navigation-buttons {
  padding-left: 50px;
  padding-right: 50px;
  padding-bottom: 30px;
  margin-top: 30px !important;
  background-color: rgba(255, 255, 255, 0.95);
  border-top: 1px solid #e0e0e0;
  border-radius: 8px;
  backdrop-filter: blur(5px);
  position: relative;
  z-index: 50;
}

/* Style des boutons de navigation en mode sombre */
.theme-dark .navigation-buttons {
  background-color: rgba(44, 62, 80, 0.95);
  border-top-color: #34495e;
  color: #ecf0f1;
}

/* 🎛️ Styles pour les paramètres de page */
.page-with-parameters {
  margin: 0 auto;
  transition: all 0.3s ease;
}

/* Largeurs pour colonne simple */
.page-with-parameters.layout-single.width-60 {
  max-width: var(--content-width, 60%);
}

.page-with-parameters.layout-single.width-70 {
  max-width: var(--content-width, 70%);
}

.page-with-parameters.layout-single.width-80 {
  max-width: var(--content-width, 80%);
}

.page-with-parameters.layout-single.width-100 {
  max-width: var(--content-width, 100%);
}

/* Thèmes */
.page-with-parameters.theme-dark {
  background-color: #2c3e50;
  color: #ecf0f1;
  border-radius: 8px;
  padding: 20px;
}

.page-with-parameters.theme-light {
  background-color: #ffffff;
  color: #2c3e50;
}

/* Stimulus containers */
.stimulus-container {
  margin: 20px 0;
  padding: 15px;
  background-color: #f8f9fa;
  border-left: 4px solid #007bff;
  border-radius: 4px;
}

/* Stimulus dans les colonnes : doit scroller si trop haut */
.stimulus-column .stimulus-container {
  margin: 0;
  max-height: none; /* Pas de limite interne, c'est la colonne qui limite */
}

.stimulus-top {
  margin-bottom: 30px;
}

.stimulus-bottom {
  margin-top: 30px;
}

.theme-dark .stimulus-container {
  background-color: #34495e;
  border-left-color: #3498db;
  color: #ecf0f1;
}

/* 🎛️ Support pour colonnes doubles : seul le stimulus utilise les colonnes */
.page-double-column {
  display: flex;
  gap: 0;
  align-items: flex-start;
  margin-bottom: 3rem; /* Espace pour les boutons de navigation */
  position: relative;
}

/* Les colonnes dans la disposition double */
.stimulus-column {
  /* Colonne qui contient le stimulus - elle s'adapte à la hauteur des items */
  padding: 0 15px;
  flex-shrink: 0;
  position: sticky;
  top: 80px; /* Position en dessous de la navbar */
  align-self: flex-start;
  max-height: calc(100vh - 100px); /* Hauteur maximale = hauteur viewport - marges */
  overflow-y: auto; /* Permettre le scroll si le contenu est trop haut */
}

.items-column {
  /* Colonne qui contient les items - définit la hauteur du conteneur */
  padding: 0 15px;
  flex-shrink: 0;
  flex-grow: 1;
}

.items-column .pageContainer {
  /* S'assurer que les items restent dans une seule colonne */
  display: block;
}

.items-column .itemContainer {
  /* Chaque item occupe toute la largeur de sa colonne */
  width: 100%;
  margin-bottom: 1rem;
}

/* 📏 Séparateur redimensionnable entre les colonnes */
.column-separator {
  width: 8px;
  background: linear-gradient(to right, transparent, #d0d0d0 50%, transparent);
  border-left: 1px solid #c0c0c0;
  border-right: 1px solid #c0c0c0;
  cursor: col-resize;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 20px;
  transition: all 0.3s ease;
  user-select: none;
  position: sticky;
  top: 80px;
  flex-shrink: 0;
  align-self: flex-start;
  min-height: calc(100vh - 100px);
}

.column-separator:hover {
  background: linear-gradient(to right, transparent, #007bff 50%, transparent);
  border-left: 2px solid #0056b3;
  border-right: 2px solid #0056b3;
  width: 12px;
  box-shadow: 0 0 10px rgba(0, 123, 255, 0.3);
}

.column-separator:active {
  background: linear-gradient(to right, transparent, #0056b3 50%, transparent);
  border-left: 2px solid #003d82;
  border-right: 2px solid #003d82;
}

.separator-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 16px;
  padding: 8px 2px;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  transition: all 0.3s ease;
  position: sticky;
  top: 50%;
  transform: translateY(-50%);
  height: 60px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.column-separator:hover .separator-handle {
  color: #ffffff;
  background-color: #007bff;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
  transform: translateY(-50%) scale(1.1);
}

.column-separator:active .separator-handle {
  color: #ffffff;
  background-color: #0056b3;
  box-shadow: 0 2px 8px rgba(0, 86, 179, 0.5);
}

/* 🔧 Fonctionnalité sticky pour le stimulus */
.stimulus-sticky {
  position: fixed !important;
  top: 20px;
  z-index: 100;
  background: rgba(248, 249, 250, 0.95);
  backdrop-filter: blur(5px);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

/* Ajustements pour thème sombre */
.theme-dark .stimulus-sticky {
  background: rgba(52, 73, 94, 0.95);
  color: #ecf0f1;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

/* Styles pour les stimulus en colonnes latérales */
.stimulus-left,
.stimulus-right {
  /* Position sticky déjà gérée par les classes conditionnelles */
}

.stimulus-left {
  margin-right: 0;
}

.stimulus-right {
  margin-left: 0;
}

/* Styles spécifiques pour les positions fixed */
.stimulus-top.stimulus-sticky {
  /* Stimulus en haut qui devient fixed */
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 800px;
}

.stimulus-bottom.stimulus-sticky {
  /* Stimulus en bas qui devient fixed */
  bottom: 20px;
  top: auto;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 800px;
}

.stimulus-left.stimulus-sticky {
  /* Stimulus à gauche fixed */
  top: 20px;
  left: 20px;
  width: 35%;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  margin-top: 250px;
}

.stimulus-right.stimulus-sticky {
  /* Stimulus à droite fixed */
  top: 20px;
  right: 20px;
  width: 35%;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  margin-top: 250px;
}

/* Amélioration visuelle lors du scroll */
.stimulus-sticky:hover {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.theme-dark .stimulus-sticky:hover {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
}
</style>